import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NodeService } from './node.service';
import { RecorderRuntimeService } from './recorder-runtime.service';
import { RecorderStateKey } from './recorder-runtime.types';

interface DesktopUpdateProgress {
    phase: 'downloading' | 'verifying' | 'ready';
    file?: string;
    completedFiles: number;
    totalFiles: number;
    receivedBytes: number;
    totalBytes: number;
    percent: number;
}

/** Coordinates desktop patch checks for the lifetime of the application. */
@Injectable({ providedIn: 'root' })
export class DesktopUpdateRuntimeService implements OnDestroy {
    private readonly pollIntervalMs = 5 * 60 * 1000;
    private started = false;
    private updateRequired = false;
    private checkInFlight = false;
    private state = RecorderStateKey.INITIALIZING;
    private stateSubscription: Subscription | null = null;
    private pollTimer: any = null;
    private restartTimer: any = null;
    private modal: HTMLElement | null = null;
    private statusElement: HTMLElement | null = null;
    private detailElement: HTMLElement | null = null;
    private progressElement: HTMLElement | null = null;
    private progressValueElement: HTMLElement | null = null;
    private actionButton: HTMLButtonElement | null = null;
    private progressListener: ((event: unknown, progress: DesktopUpdateProgress) => void) | null = null;
    private restartFailureListener: ((event: unknown, message: string) => void) | null = null;

    constructor(
        private readonly node: NodeService,
        private readonly recorderRuntime: RecorderRuntimeService
    ) { }

    start(): void {
        if (this.started || !this.node.isAvailable()) return;
        this.started = true;
        this.stateSubscription = this.recorderRuntime.states.subscribe(state => {
            this.state = state;
            this.check();
        });
        this.pollTimer = setInterval(() => this.check(), this.pollIntervalMs);
    }

    private async check(): Promise<void> {
        if (this.updateRequired || this.checkInFlight || !this.isSafeToUpdate()) return;
        this.checkInFlight = true;
        try {
            if (!await this.node.ipcRenderer.invoke('desktop-update:check')) return;
            this.updateRequired = true;
            this.showModal();
            this.setModalState('Update available', 'Restart W3Booster to download and install the verified update.', 0);
            this.setAction('Restart and Install', () => this.downloadUpdate());
        } catch (error) {
            if (this.updateRequired) this.showDownloadError(error);
            else console.warn('Desktop update check failed:', error);
        } finally {
            this.checkInFlight = false;
        }
    }

    private async downloadUpdate(): Promise<void> {
        this.setModalState('Downloading update', 'Preparing the verified update package…', 0);
        this.setAction('', null);
        this.attachIpcListeners();
        try {
            await this.node.ipcRenderer.invoke('desktop-update:download');
            this.startRestartCountdown();
        } catch (error) {
            this.showDownloadError(error);
        }
    }

    private startRestartCountdown(): void {
        let seconds = 3;
        const renderCountdown = () => {
            this.setModalState('Update ready', `W3Booster restarts in ${seconds}…`, 100);
            this.setAction('', null);
        };
        renderCountdown();
        this.restartTimer = setInterval(() => {
            seconds--;
            if (seconds <= 0) {
                if (this.restartTimer) clearInterval(this.restartTimer);
                this.restartTimer = null;
                this.setModalState('Update ready', 'W3Booster restarts in 0…', 100);
                this.restart();
                return;
            }
            renderCountdown();
        }, 1000);
    }

    private restart(): void {
        this.setModalState('Restarting W3Booster', 'The update will be installed before the client opens again.', 100);
        this.setAction('', null);
        this.node.ipcRenderer.send('desktop-update:restart');
    }

    private showModal(): void {
        if (this.modal) return;
        const backdrop = document.createElement('div');
        backdrop.className = 'w3-update-backdrop';
        backdrop.setAttribute('role', 'dialog');
        backdrop.setAttribute('aria-modal', 'true');
        backdrop.setAttribute('aria-labelledby', 'w3-update-title');

        const dialog = document.createElement('section');
        dialog.className = 'w3-update-dialog w3-dialog';
        const brand = document.createElement('img');
        brand.className = 'w3-update-brand';
        brand.src = 'assets/images/brand/w3booster-booster.webp?v=2';
        brand.alt = 'W3Booster';

        const copy = document.createElement('div');
        copy.className = 'w3-update-copy w3-dialog-header';
        const eyebrow = document.createElement('span');
        eyebrow.className = 'w3-update-eyebrow w3-dialog-kicker';
        eyebrow.textContent = 'Required update';
        const title = document.createElement('h2');
        title.id = 'w3-update-title';
        title.textContent = 'Updating W3Booster';
        const description = document.createElement('p');
        description.textContent = 'The update starts only after you confirm that W3Booster may restart.';
        copy.append(brand, eyebrow, title, description);

        const body = document.createElement('div');
        body.className = 'w3-update-body w3-dialog-body';
        this.statusElement = document.createElement('strong');
        this.statusElement.className = 'w3-update-status';
        this.detailElement = document.createElement('p');
        this.detailElement.className = 'w3-update-detail';
        const progressTrack = document.createElement('div');
        progressTrack.className = 'w3-update-progress';
        progressTrack.setAttribute('role', 'progressbar');
        progressTrack.setAttribute('aria-valuemin', '0');
        progressTrack.setAttribute('aria-valuemax', '100');
        this.progressElement = progressTrack;
        this.progressValueElement = document.createElement('span');
        progressTrack.append(this.progressValueElement);
        this.actionButton = document.createElement('button');
        this.actionButton.type = 'button';
        this.actionButton.className = 'w3-update-action w3-dialog-action w3-dialog-primary';
        body.append(this.statusElement, this.detailElement, progressTrack);
        const footer = document.createElement('footer');
        footer.className = 'w3-dialog-footer';
        footer.append(this.actionButton);

        dialog.append(copy, body, footer);
        backdrop.append(dialog);
        document.body.append(backdrop);
        this.modal = backdrop;
    }

    private attachIpcListeners(): void {
        if (!this.progressListener) {
            this.progressListener = (_event, progress) => this.showProgress(progress);
            this.node.ipcRenderer.on('desktop-update:progress', this.progressListener);
        }
        if (!this.restartFailureListener) {
            this.restartFailureListener = (_event, message) => this.showRestartError(message);
            this.node.ipcRenderer.on('desktop-update:restart-failed', this.restartFailureListener);
        }
    }

    private showProgress(progress: DesktopUpdateProgress): void {
        const percent = Math.max(0, Math.min(100, Number(progress.percent) || 0));
        const fileProgress = progress.totalFiles
            ? `${Math.min(progress.completedFiles + 1, progress.totalFiles)} of ${progress.totalFiles} files`
            : 'Preparing files';
        const phase = progress.phase === 'verifying' ? 'Verifying update' : 'Downloading update';
        const size = progress.totalBytes > 0
            ? ` · ${this.formatBytes(progress.receivedBytes)} of ${this.formatBytes(progress.totalBytes)}`
            : '';
        this.setModalState(phase, `${fileProgress}${size}`, percent);
    }

    private showDownloadError(error: unknown): void {
        console.warn('Desktop update download failed:', error);
        this.setModalState('Download interrupted', 'W3Booster could not download the update. Check your connection and try again.', 0);
        this.setAction('Retry Download', () => this.downloadUpdate());
    }

    private showRestartError(message: string): void {
        console.warn('Desktop restart failed:', message);
        this.setModalState('Restart required', 'W3Booster could not restart automatically. Close and reopen the client to finish installing.', 100);
        this.setAction('Try Restart Again', () => this.restart());
    }

    private setModalState(status: string, detail: string, percent: number): void {
        if (this.statusElement) this.statusElement.textContent = status;
        if (this.detailElement) this.detailElement.textContent = detail;
        if (this.progressValueElement) this.progressValueElement.style.width = `${percent}%`;
        if (this.progressElement) this.progressElement.setAttribute('aria-valuenow', String(percent));
    }

    private setAction(label: string, action: (() => void) | null): void {
        if (!this.actionButton) return;
        this.actionButton.textContent = label;
        this.actionButton.hidden = !action;
        this.actionButton.parentElement.hidden = !action;
        this.actionButton.onclick = action;
    }

    private formatBytes(bytes: number): string {
        if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    private isSafeToUpdate(): boolean {
        return this.state === RecorderStateKey.WAITING_FOR_W3
            || this.state === RecorderStateKey.WAITING_FOR_64BIT_W3
            || this.state === RecorderStateKey.WAITING_FOR_GAME
            || this.state === RecorderStateKey.GAME_ENDED
            || this.state === RecorderStateKey.RECORDER_STOPS;
    }

    ngOnDestroy(): void {
        this.stateSubscription?.unsubscribe();
        this.stateSubscription = null;
        if (this.pollTimer) clearInterval(this.pollTimer);
        this.pollTimer = null;
        if (this.restartTimer) clearInterval(this.restartTimer);
        this.restartTimer = null;
        if (this.progressListener) this.node.ipcRenderer.removeListener('desktop-update:progress', this.progressListener);
        if (this.restartFailureListener) this.node.ipcRenderer.removeListener('desktop-update:restart-failed', this.restartFailureListener);
        this.progressListener = null;
        this.restartFailureListener = null;
        this.modal?.remove();
        this.modal = null;
        this.started = false;
    }
}
